"use client"

import { useTransportOrganizationContext } from "@/transport"
import { useOrganization } from "@clerk/nextjs"

import { useQueryParams } from "@/hooks/useQueryParams"

import { UsageRemainingChip } from "../components/UsageRemainingChip"
import { useBillingSubscription } from "../hooks/useBillingSubscription"
import { useBillingUsage } from "../hooks/useBillingUsage"
import { usageBandState, usageLadderApplies } from "../utils/usageBand"

// Settings owns this route; billing names the path rather than importing the
// settings module for one string.
const USAGE_SETTINGS_PATH = "/settings/usage"

/**
 * The allowance at the point of spend, for a launcher to place beside its Launch
 * button. Renders nothing until an organization is on a rung, so a quiet
 * organization sees no extra chrome.
 */
export const LaunchUsageChip = () => {
  const { organization } = useOrganization()
  const { isAuthLoaded, isOrganizationSynced, organizationSlug } =
    useTransportOrganizationContext()
  const queryParams = useQueryParams()

  // The billing API resolves the organization from the route, so asking before
  // Clerk and the route agree reads another tenant's usage or 404s.
  const enabled =
    organization != null &&
    isAuthLoaded &&
    isOrganizationSynced &&
    organizationSlug !== null &&
    organization.slug === organizationSlug

  const { data: subscription } = useBillingSubscription({ enabled })
  const { data: usage } = useBillingUsage({ enabled })

  if (!enabled) {
    return null
  }

  const state = usageBandState(subscription ?? null, usage ?? null)
  if (state === null || subscription == null) {
    return null
  }
  // The warning rungs are the self-serve policy, so they are only spoken to the
  // organizations that policy describes — the same gate the shell banner and the
  // usage page apply. Being out of allowance is a fact rather than an offer, so
  // the top rung still speaks whatever the billing source.
  if (!usageLadderApplies(subscription) && state.band !== "over") {
    return null
  }

  return (
    <UsageRemainingChip
      state={state}
      subscription={subscription}
      href={queryParams.getHref(USAGE_SETTINGS_PATH, organizationSlug)}
    />
  )
}
